import React, { useState } from 'react';
import axios from 'axios';

import type { ButtonProps } from '~/components/core/Atomic/Buttons/Button';
import Button from '~/components/core/Atomic/Buttons/Button';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';

import type { CommunicationChannel } from './AttachCommunicationDialogs';
import { AttachOtherContacts, AttachPhysicalMail } from './AttachCommunicationDialogs';

type CommunicationAttachExtraParams = {
  contact_id?: number | '';
  should_create_new_contact?: boolean;
};

interface CommunicationAttachToKnownClaimButtonProps {
  claimId: number;
  communicationId: number;
  communicationChannel: string;
  onAttach: (claimId: number) => Promise<void> | void;
  onAttachOverride?: (claimId: number, attachExtraParams: CommunicationAttachExtraParams) => Promise<void> | void;
  buttonComponent?: React.ComponentType<ButtonProps>;
}

// Default button component
const DefaultButton: React.FC<ButtonProps> = ({ children, ...buttonProps }) => (
  <Button color="primary" {...buttonProps}>
    {children}
  </Button>
);

export const CommunicationAttachToKnownClaimButton: React.FC<CommunicationAttachToKnownClaimButtonProps> = ({
  claimId,
  communicationId,
  communicationChannel,
  onAttach,
  onAttachOverride,
  buttonComponent: CustomButton = DefaultButton,
}) => {
  const [showAttachPhysicalMail, setShowAttachPhysicalMail] = useState(false);
  const [showOtherContactsOptions, setShowOtherContactsOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAttach = async (attachExtraParams: CommunicationAttachExtraParams = {}) => {
    if (onAttachOverride) {
      await onAttachOverride(claimId, attachExtraParams);
      return;
    }

    await axios.post(`/api/v1/communications/${communicationId}/attach`, {
      claim_id: claimId,
      ...attachExtraParams,
    });
    await onAttach(claimId);
  };

  const handleSelectClaim = () => {
    setIsSubmitting(true);
    if (['email', 'phone_call', 'sms'].includes(communicationChannel)) {
      setShowOtherContactsOptions(true);
      setIsSubmitting(false);
    } else if (communicationChannel === 'physical_mail') {
      setShowAttachPhysicalMail(true);
      setIsSubmitting(false);
    } else {
      reportErrorInProductionOrThrow(
        `Unknown communication type when attaching communication ${communicationId} to claim ${claimId}`
      );
      setIsSubmitting(false);
    }
  };

  const handleAttachOtherContact = async (values: {
    contact_id: number | '';
    contact_attachment_choice: string | number;
  }) => {
    try {
      await handleAttach({
        contact_id: values.contact_id,
        should_create_new_contact: values.contact_attachment_choice === 'new_contact',
      });
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttachPhysicalMail = async (values: { contact_id: number | ''; contact_full_name: string }) => {
    try {
      await handleAttach({
        contact_id: values.contact_id,
      });
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomButton onClick={handleSelectClaim} disabled={isSubmitting}>
        <span className="whitespace-nowrap">Attach to claim</span>
      </CustomButton>
      {showOtherContactsOptions && (
        <AttachOtherContacts
          claimId={claimId}
          communicationId={communicationId}
          communicationChannel={communicationChannel as CommunicationChannel}
          onClose={() => {
            setShowOtherContactsOptions(false);
            setIsSubmitting(false);
          }}
          onSubmit={handleAttachOtherContact}
        />
      )}
      {showAttachPhysicalMail && (
        <AttachPhysicalMail
          claimId={claimId}
          onClose={() => {
            setShowAttachPhysicalMail(false);
            setIsSubmitting(false);
          }}
          onUpdate={handleAttachPhysicalMail}
        />
      )}
    </>
  );
};
